const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api`;

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('burnz_token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// Auth API
export const authAPI = {
  login: async (walletAddress, signature) => {
    return apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ wallet_address: walletAddress, signature }),
    });
  },
  
  register: async (walletAddress, walletType = 'nami') => {
    return apiCall('/auth/connect', {
      method: 'POST',
      body: JSON.stringify({ wallet_address: walletAddress, wallet_type: walletType }),
    });
  },
  
  connect: async (walletAddress, walletType = 'nami') => {
    return apiCall('/auth/connect', {
      method: 'POST',
      body: JSON.stringify({ wallet_address: walletAddress, wallet_type: walletType }),
    });
  },
  
  getProfile: async () => {
    return apiCall('/auth/me');
  },
};

// Pools API (Legacy - use teamsAPI instead)
export const poolsAPI = {
  getAll: async (poolType = null) => {
    const params = poolType ? `?pool_type=${poolType}` : '';
    return apiCall(`/pools${params}`);
  },
  
  getById: async (poolId) => {
    return apiCall(`/pools/${poolId}`);
  },
  
  join: async (poolId) => {
    return apiCall(`/pools/${poolId}/join`, { method: 'POST' });
  },
  
  getMembers: async (poolId) => {
    return apiCall(`/pools/${poolId}/members`);
  },
};

// Teams API (Replaces Pools)
export const teamsAPI = {
  getAll: async (teamType = null) => {
    const params = teamType ? `?team_type=${teamType}` : '';
    const response = await apiCall(`/teams${params}`);
    // Normalize team data for frontend
    if (response.teams) {
      response.teams = response.teams.map(team => normalizeTeam(team));
    }
    return response;
  },
  
  getById: async (teamId) => {
    const response = await apiCall(`/teams/${teamId}`);
    if (response.team) {
      response.team = normalizeTeam(response.team);
    }
    return response;
  },
  
  join: async (teamId) => {
    return apiCall(`/teams/${teamId}/join`, { method: 'POST' });
  },
  
  leave: async (teamId) => {
    return apiCall(`/teams/${teamId}/leave`, { method: 'POST' });
  },
  
  getLeaderboard: async (period = 'all') => {
    const response = await apiCall(`/teams/leaderboard?period=${period}`);
    // Normalize leaderboard data
    if (response.leaderboard) {
      response.leaderboard = response.leaderboard.map(team => normalizeTeam(team));
    }
    return response;
  },
  
  create: async (data) => {
    return apiCall('/teams', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Helper to normalize team data from API to frontend format
const normalizeTeam = (team) => ({
  ...team,
  // Map API fields to frontend expected fields
  type: team.team_type || team.type || 'recovery',
  image: team.image_url || team.image || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400',
  score: team.team_score || team.score || 0,
  memberCount: team.member_count || team.memberCount || 0,
  activeMembers: team.active_members || team.activeMembers || 0,
  totalXP: team.total_xp || team.totalXP || 0,
  totalBurned: team.total_burnz_burned || team.totalBurned || 0,
  weeklyXP: team.weekly_xp || team.weeklyXP || 0,
  weeklyBurns: team.weekly_burns || team.weeklyBurns || 0,
  originalProject: team.original_project_name || team.originalProject || null,
});

// User Overview API (XP-First System)
export const userAPI = {
  getOverview: async () => {
    return apiCall('/users/me/overview');
  },
  
  convertXP: async (xpAmount) => {
    return apiCall('/users/convert-xp', {
      method: 'POST',
      body: JSON.stringify({ xp_amount: xpAmount }),
    });
  },
  
  getLeaderboard: async (period = 'all') => {
    return apiCall(`/users/leaderboard?period=${period}`);
  },
  
  getNFTs: async () => {
    return apiCall('/user/nfts');
  },
  
  claimTokens: async (tokenType, amount) => {
    return apiCall('/tokens/claim', {
      method: 'POST',
      body: JSON.stringify({ token_type: tokenType, amount }),
    });
  },
};

// Quests API (Updated for Web2 model)
export const questsAPI = {
  getAll: async (questType = null) => {
    const params = questType ? `?quest_type=${questType}` : '';
    return apiCall(`/quests${params}`);
  },
  
  getCampaignQuests: async (questType = null) => {
    const params = questType ? `?quest_type=${questType}` : '';
    return apiCall(`/campaigns/quests${params}`);
  },
  
  getById: async (questId) => {
    return apiCall(`/quests/${questId}`);
  },
  
  completeTask: async (taskId, proof = null) => {
    return apiCall(`/quests/tasks/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ proof }),
    });
  },
  
  verifyTask: async (taskId, proofUrl = null, proofScreenshot = null) => {
    return apiCall(`/quests/tasks/${taskId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ 
        task_id: taskId,
        proof_url: proofUrl,
        proof_screenshot: proofScreenshot 
      }),
    });
  },
};

// Campaigns API (Web2-Friendly Partner Portal)
export const campaignsAPI = {
  create: async (data) => {
    return apiCall('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  getAll: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/campaigns${params}`);
  },
  
  getById: async (campaignId) => {
    return apiCall(`/campaigns/${campaignId}`);
  },
  
  createQuest: async (data) => {
    return apiCall('/campaign-quests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Partner Application API
export const partnerAPI = {
  apply: async (data) => {
    return apiCall('/partner/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  // Admin endpoints
  getApplications: async (status = null) => {
    const params = status ? `?status=${status}` : '';
    return apiCall(`/admin/partner-applications${params}`);
  },
  
  updateApplicationStatus: async (applicationId, status) => {
    return apiCall(`/admin/partner-applications/${applicationId}/status?status=${status}`, {
      method: 'PUT',
    });
  },
};

// Dump API
export const dumpAPI = {
  submit: async (poolId, nftIds) => {
    return apiCall('/dump/submit', {
      method: 'POST',
      body: JSON.stringify({ pool_id: poolId, nft_ids: nftIds }),
    });
  },
  
  getHistory: async () => {
    return apiCall('/dump/history');
  },
};

// Stories API
export const storiesAPI = {
  getAll: async () => {
    return apiCall('/stories');
  },
  
  getById: async (storyId) => {
    return apiCall(`/stories/${storyId}`);
  },
};

// Stats API
export const statsAPI = {
  getDashboard: async () => {
    return apiCall('/dashboard');
  },
  
  getLeaderboard: async () => {
    return apiCall('/leaderboard');
  },
};

// Rewards API (Referral Rewards System)
export const rewardsAPI = {
  getTiers: async () => {
    return apiCall('/rewards/tiers');
  },
  
  getMyProgress: async () => {
    return apiCall('/rewards/my-progress');
  },
  
  claimTierReward: async (tier) => {
    return apiCall('/rewards/claim', {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  },
  
  getLeaderboard: async (limit = 20) => {
    return apiCall(`/rewards/leaderboard?limit=${limit}`);
  },
};

export default {
  auth: authAPI,
  pools: poolsAPI,
  teams: teamsAPI,
  quests: questsAPI,
  dump: dumpAPI,
  stories: storiesAPI,
  stats: statsAPI,
  user: userAPI,
  campaigns: campaignsAPI,
  partner: partnerAPI,
  rewards: rewardsAPI,
};
